import { truncateString } from "@ihs-widget/utils/functions";
import { useEffect, useRef, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import SmallSpinner from "@ihs-widget/components/common/SmallSpinner";
import { useLanguage } from "@ihs-widget/utils/hooks/useLanguage";
import { useTranslations } from "../../../../../../translation/TranslationContext";
import SmallSpinnerTimeout from "@ihs-widget/components/common/SmallSpinnerTimeout";
import { useColorContext } from "@ihs-widget/stores/ColorContext";

interface ScoreWithLabelProps {
    label: string;
    home: number;
    away: number;
    isTwoRows?: boolean;
}

const ScoreWithLabel = ({label, home, away, isTwoRows = false}: ScoreWithLabelProps) => {
    const language = useLanguage();
    const translations = useTranslations(language);
    
    const translatedLabel = useMemo(() => {
        const t = translations[label as keyof typeof translations] as unknown as string;
        return typeof t === 'string' ? t : '';
    }, [translations, label]);
    
    const labelRef = useRef<HTMLDivElement>(null);
    const [displayLabel, setDisplayLabel] = useState('');
    const { colors, isDynamicColors } = useColorContext();

    useEffect(() => {
        const truncateScoreWithLabel = () => {
            const element = labelRef.current;
            if (!element) return;

            const words = translatedLabel.split(' ');
            let result = '';
            let line = 1;

            for (let i = 0; i < words.length; i++) {
                const testString = result + (result ? ' ' : '') + words[i];
                element.textContent = testString;

                if (element.clientHeight >= 32) { 
                    if (line === 2) {
                        result = result.trim() + '...';
                        break;
                    }
                    result += '\n' + words[i];
                    line++;
                } else {
                    result = testString;
                }
            }

            setDisplayLabel(truncateString(result, 29));
        };
        
        const debouncedTruncateScoreWithLabel = debounce(truncateScoreWithLabel, 250);
        
        const timeoutId = setTimeout(truncateScoreWithLabel, 1);
        window.addEventListener('resize', debouncedTruncateScoreWithLabel);
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', debouncedTruncateScoreWithLabel);
            debouncedTruncateScoreWithLabel.cancel();
        };
    }, [translatedLabel]);

    return (
        <div 
            data-testid={`${label.toLowerCase().replace(' ', '-')}`} // for example sc_stats_goal_shots_off_target
            className="w-full h-[24px] flex flex-row justify-center items-center gap-[8px] z-30">
            <div data-testid={`home-value`} className="w-[17.5%] flex justify-end items-center">
                <div className="h-[24px] w-fit px-[4px] bg-monoY dark:bg-base110 
                    flex flex-row justify-center items-center rounded-[6px] text-[16px] leading-[24px]
                    font-medium font-robotoMedium"
                    style={{ color: `${colors?.homeColor}` }}
                    >
                    {home !== null ? home : <SmallSpinnerTimeout />}
                </div> 
            </div>
            {isTwoRows ? (
                <div ref={labelRef} className="max-w-[30%] text-[12px] leading-[16px] font-medium font-robotoMedium bg-monoY dark:bg-base110  
                text-base120 dark:text-monoY rounded-[6px] px-[4px] text-ellipsis max-h-[32px] flex items-center justify-center align-middle
                text-center whitespace-pre-wrap">
                    {displayLabel}
                </div>
            ) : (
                <div className="max-w-[70%] text-[12px] leading-[16px] font-medium font-robotoMedium bg-monoY dark:bg-base110  
                text-base120 dark:text-monoY rounded-[6px] px-[4px] text-nowrap overflow-hidden text-ellipsis max-h-[16px] whitespace-pre-wrap">
                    {translatedLabel}
                </div>
            )}
            <div data-testid={`away-value`} className="w-[17.5%] flex justify-start items-center">
                <div 
                    className="h-[24px] w-fit px-[4px] bg-monoY dark:bg-base110
                            flex flex-row justify-center items-center rounded-[6px] text-[16px] leading-[24px]
                            font-medium font-robotoMedium"
                            style={{ color: `${colors?.awayColor}` }}
                            >
                    {away !== null ? away : <SmallSpinnerTimeout />}
                </div> 
            </div>
        </div>
    )
}

export default ScoreWithLabel;